module.exports = createSnapshot

const pick = require('lodash/pick')
const get = require('lodash/get')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')

const getSnapshotId = require('./tools/get-snapshot-id')
const docToVanRecord = require('./tools/doc-to-van-record')
const { getShipmentType } = require('./tools/shipment-types')
const addComment = require('./shipment-add-comment')
const {isInvalidSnapshot} = require('./validate/snapshot')
const { DIRECT_ORDER_TYPES } = require('../allocation/config')
const PLANNING_TYPES = require('./tools/planning-types')
const { ORDER_SHIPMENT_SHARED_PROPS } = require('../order/constants')
const { SHIPMENT_VERSIONS } = require('./constants')

const OPTIONS_TO_CLONE = [
  'orderId', 'shouldReturnProducts', 'programId', 'isGpsAuthenticated', 'gpsValidationFailComment',
  'isAutomaticReturnShipment', // we need this flag to avoid updating ledger
  'externalShipmentId', // These are for shipments from external WMS's
  ...ORDER_SHIPMENT_SHARED_PROPS
]

const cleanCounts = ({counts, status, removePaymentType, doCheckSent = true}) => {
  return Object.keys(counts).reduce((clean, batchId) => {
    if ((status === 'sent' || status === 'received') && !counts[batchId].checked && doCheckSent) {
      return clean
    }

    clean[batchId] = {
      quantity: counts[batchId].quantity,
      paymentType: counts[batchId].paymentType || DIRECT_ORDER_TYPES.PAY_AS_YOU_SELL
    }

    if (Object.prototype.hasOwnProperty.call(counts[batchId], 'unitSellPrice')) {
      clean[batchId].unitSellPrice = counts[batchId].unitSellPrice
    }

    if (Object.prototype.hasOwnProperty.call(counts[batchId], 'unitSellPriceReferenceSku')) {
      clean[batchId].unitSellPriceReferenceSku = counts[batchId].unitSellPriceReferenceSku
    }

    if (Object.prototype.hasOwnProperty.call(counts[batchId], 'vat')) {
      clean[batchId].vat = counts[batchId].vat
    }

    if (Object.prototype.hasOwnProperty.call(counts[batchId], 'unitSellPriceReferenceId')) {
      clean[batchId].unitSellPriceReferenceId = counts[batchId].unitSellPriceReferenceId
    }

    if (counts[batchId].prescription) {
      clean[batchId].prescription = counts[batchId].prescription
    }

    if (counts[batchId].unitPrice) {
      clean[batchId].unitPrice = counts[batchId].unitPrice
    }

    if (counts[batchId].priceSource) {
      clean[batchId].priceSource = counts[batchId].priceSource
    }

    if (removePaymentType) {
      delete clean[batchId]['paymentType']
    }

    return clean
  }, {})
}

// get options that we just want to get into the shipment
// having same name and value. Only if they're defined
const extractOptionsToClone = (options) => {
  const optionsToCloneNames = [...OPTIONS_TO_CLONE]

  const { status, parentDocId } = options

  if (parentDocId && !(['new', 'pre-advice'].includes(status))) {
    optionsToCloneNames.push('parentDocId')
  }

  const allProps = pick(options, optionsToCloneNames)
  return omitBy(allProps, isNil) // return defined picked props
}

async function createSnapshot (state, options) {
  const {
    status,
    counts,
    shipmentTypeId = 'routine',
    planningType = PLANNING_TYPES.DEFAULT,
    overrideCreatedAt,
    dryRun = false,
    removePaymentType,
    doCheckSent,
    programId,
    funderId,
    routeId
  } = options

  if (!getShipmentType(shipmentTypeId)) {
    throw new Error(`${shipmentTypeId} is not a valid shipment type`)
  }

  const optionsToClone = extractOptionsToClone(options)

  if (overrideCreatedAt) {
    optionsToClone.orderCreatedDate = overrideCreatedAt
  }

  const id = getSnapshotId(state, options)
  const doc = {
    _id: id,
    type: 'snapshot',
    version: SHIPMENT_VERSIONS.VERSION_2,
    createdAt: overrideCreatedAt || new Date(),
    counts: dryRun ? counts : cleanCounts({counts, status, removePaymentType, doCheckSent}),
    shipmentTypeId,
    planningType,
    createdBy: state.user.name,
    ...optionsToClone
  }

  if (programId) {
    doc['programId'] = programId
  }

  if (funderId) {
    doc['funderId'] = funderId
  }

  if (routeId) {
    doc['routeId'] = routeId
  }

  const validationErrors = isInvalidSnapshot(doc)
  if (validationErrors) throw new Error(validationErrors)

  if (dryRun) return doc

  const { rev } = await state.dal.shipment.write(state, doc)
  doc._rev = rev

  const commentOptions = pick(options, 'comment', 'signature', 'name', 'phoneNumber', 'adjustmentReasons')
  const { comment, signature, name, phoneNumber, adjustmentReasons } = commentOptions

  if (comment || signature || name) {
    await addComment(state, id, comment, signature, name, doc.orderId, phoneNumber, adjustmentReasons)
    Object.assign(doc, commentOptions)
  }

  const locationId = get(state, 'user.location.id')
  return docToVanRecord(doc, locationId)
}
